import { Inject, Injectable } from '@angular/core';
import { UrlFactory, Params } from './url-factory';
import {
  ApiVersion,
  GatewayConfig,
  SHARED_GATEWAY_CONFIG,
} from '../gateway.config';

@Injectable({ providedIn: 'root' })
export class GsgGateway {
  clientIdAccNo: { [key: string]: string } = {
    metadata: 'client_id,account_number,priority',
  };

  isAWSContentHub = false;

  constructor(
    private _urlFactory: UrlFactory,
    @Inject(SHARED_GATEWAY_CONFIG) private _config: GatewayConfig,
  ) {
    this.isAWSContentHub = !!(
      _config['gsg'] && _config['gsg'].includes('caas/api/v1')
    );
  }

  contentUrl(subPath: string, params?: { [key: string]: string }) {
    return this._urlFactory.createUrl(
      'gsg',
      `contents/${subPath}`,
      params ? params : this.clientIdAccNo,
    );
  }

  chubUrl(subPath: string, params?: { [key: string]: string }) {
    return this.contentHub(
      `contents/${subPath}`,
      params ? params : this.clientIdAccNo,
    );
  }

  coverageModuleAlertUrl() {
    return this.contentHub(
      'contents/modular-alert/categories/coverage',
      this.clientIdAccNo,
    );
  }

  resourcesModuleAlertUrl() {
    return this.contentHub(
      'contents/modular-alert/categories/resources',
      this.clientIdAccNo,
    );
  }

  wellnessModuleAlertUrl() {
    return this.contentHub(
      'contents/modular-alert/categories/wellness',
      this.clientIdAccNo,
    );
  }

  directoryModuleAlertUrl() {
    return this.contentHub(
      'contents/modular-alert/categories/directory',
      this.clientIdAccNo,
    );
  }

  claimsModuleAlertUrl() {
    return this.contentHubNoCache('contents/modular-alert/categories/claims');
  }

  contactUsModuleAlertUrl() {
    return this.contentHubNoCache(
      'contents/modular-alert/categories/contact-us',
    );
  }

  claimsCOVIDTestNames() {
    return this.contentHub(
      'contents/modular-content/categories/claims/subcategories/covidTests',
    );
  }

  dashboardContentUrl(): string {
    return this.contentHubNoCache(
      `contents/modular-content/categories/dashboard/subcategories/coveragetile`,
    );
  }

  telehealthContentUrl(): string {
    return this.contentHub(
      'contents/modular-content/categories/directory/subcategories/telehealth',
    );
  }

  accountsContentUrl(): string {
    return this.contentHub('contents/modular-content/categories/accounts');
  }
  accountsCHTContentUrl(): string {
    return this.contentHub(
      'contents/modular-content/categories/accounts/subcategories/cht',
    );
  }
  accountsPhoneNumberUrl(param: string): string {
    return this.contentHub(
      'contents/phone-number/categories/primary/subcategories/std',
      { 'business-title': param },
    );
  }
  accountsAlertUrl(): string {
    return this.contentHub('contents/modular-alert/categories/accounts');
  }
  wellnessContentUrl(type = 'wellness', getUniqueId = false): string {
    return this.contentHub(
      `contents/${type}${getUniqueId ? '?metadata=unique_id' : ''}`,
    );
  }

  coverageDetailContentUrl(category: string, subCategory?: string): string {
    return this.contentHub(
      `contents/coverage-detail/categories/${category}${
        subCategory ? `/subcategories/${subCategory}` : ''
      }`,
    );
  }

  pharmacyMedicareUrl(): string {
    return this.contentHub(
      `contents/coverage-detail/categories/standalone-pharmacy`,
    );
  }

  eapContentUrl(): string {
    return this.contentHub(
      'contents/modular-content/categories/coverages/subcategories/eap',
    );
  }

  homeDeliveryContentURL(): string {
    return this.contentHub('contents/modular-alert/categories/homedelivery');
  }

  reimburmentContentURL(): string {
    return this.contentHub(
      'contents/modular-alert/categories/accounts/subcategories/reimbursementsummary',
    );
  }

  private contentHub(
    subPath: string,
    params?: Params<unknown>,
    apiVersion: ApiVersion = ApiVersion.v2,
  ): string {
    return this._urlFactory.createUrl(
      'gsg',
      this.getPath(subPath, apiVersion),
      params,
    );
  }

  private contentHubNoCache(
    subPath: string,
    params?: Params<unknown>,
    apiVersion: ApiVersion = ApiVersion.v2,
  ): string {
    return this._urlFactory.createUrlWithTimestamp(
      'gsg',
      this.getPath(subPath, apiVersion),
      params,
    );
  }

  private getPath(subPath: string, apiVersion: ApiVersion) {
    return this.isAWSContentHub
      ? `${subPath}`
      : `chub/wdc/contenthub/api/${apiVersion}/${subPath}`;
  }

  healthLibraryContentURL(request?: Params<unknown>): string {
    return this.contentHub('contents/health-library', request);
  }

  incentivesFAQContentURL(): string {
    return this.contentHub('contents/faqs');
  }

  myCignaContactUsContentUrl(type = 'contact-us'): string {
    return this.contentHubNoCache(`contents/${type}`);
  }
}
